import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaFileExcel, FaDownload } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { allowDevMockFallback } from "../../utils/devMockFallback";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getUserName } from "../../utils/auth";
import { normalizePhone, parseCsv } from "../../utils/parseCsv";
import { isApprovedTemplate } from "../../utils/templatePreview";
import CampaignTemplatePicker from "./CampaignTemplatePicker";

const STEPS = [
  "Campaign Name",
  "Upload CSV",
  "Map Columns",
  "Choose Template",
  "Schedule & Launch",
];

const ATTRIBUTE_OPTIONS = [
  { value: "", label: "Select an attribute" },
  { value: "name", label: "Name" },
  { value: "phone", label: "Phone / Mobile" },
  { value: "tags", label: "Tags" },
  { value: "email", label: "Email" },
];

const MOCK_TEMPLATES = [
  {
    _id: "69f6ffb2b1ae76dbff45c051",
    template_id: "4513437452222207",
    name: "democonfirmation_classopt",
    category: "MARKETING",
    template_status: "APPROVED",
  },
];

const SAMPLE_CSV = `name,mobile,tags
Student One,9876543210,Class11
Student Two,9876543211,Class10`;

const ExcelCampaign = () => {
  const { projectId, phoneNoId, wabaId } = useProjectPhone();

  const [step, setStep] = useState(0);
  const [campaignName, setCampaignName] = useState("");

  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [headerMappings, setHeaderMappings] = useState({});
  const [countryCode, setCountryCode] = useState("91");

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState("");
  const [error, setError] = useState("");

  const phoneHeader = useMemo(
    () =>
      Object.entries(headerMappings).find(([, m]) => m.field === "phone")?.[0],
    [headerMappings],
  );

  const excelData = useMemo(() => {
    if (!phoneHeader) return [];
    return csvRows
      .map((row) => {
        const phoneNumber = normalizePhone(row[phoneHeader], countryCode);
        if (!phoneNumber) return null;
        const entry = { phoneNumber, ...row };
        const nameKey = Object.entries(headerMappings).find(
          ([, m]) => m.field === "name",
        )?.[0];
        if (nameKey) entry.name = row[nameKey];
        return entry;
      })
      .filter(Boolean);
  }, [csvRows, headerMappings, phoneHeader, countryCode]);

  const csvheadersmap = useMemo(
    () =>
      csvHeaders.map((header) => ({
        header,
        field: headerMappings[header]?.field || "",
        default: headerMappings[header]?.default || "",
      })),
    [csvHeaders, headerMappings],
  );

  const fetchTemplates = useCallback(async () => {
    if (!phoneNoId) return;
    setLoadingTemplates(true);
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?page=1&limit=100&sortBy=createdAt&sortOrder=desc`,
      );
      const list = res?.data || res?.templates || [];
      const next =
        Array.isArray(list) && list.length
          ? list
          : allowDevMockFallback()
            ? MOCK_TEMPLATES
            : [];
      setTemplates(next);
      setSelectedTemplate((prev) =>
        prev && isApprovedTemplate(prev) ? prev : null,
      );
    } catch {
      setTemplates(allowDevMockFallback() ? MOCK_TEMPLATES : []);
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNoId]);

  useEffect(() => {
    if (step === 3) fetchTemplates();
  }, [step, fetchTemplates]);

  const handleFile = (file) => {
    if (!file) return;
    setCsvFile(file);
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCsv(e.target.result || "");
      setCsvHeaders(headers);
      setCsvRows(rows);
      const initial = {};
      headers.forEach((h) => {
        const lower = h.toLowerCase();
        let field = "";
        if (lower.includes("name")) field = "name";
        else if (lower.includes("mobile") || lower.includes("phone")) {
          field = "phone";
        } else if (lower.includes("tag")) field = "tags";
        initial[h] = { field, default: "" };
      });
      setHeaderMappings(initial);
    };
    reader.readAsText(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_campaign.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const canNext = () => {
    if (step === 0) return campaignName.trim().length > 0;
    if (step === 1) return csvRows.length > 0 && csvHeaders.length > 0;
    if (step === 2) return Boolean(phoneHeader) && excelData.length > 0;
    if (step === 3) return Boolean(selectedTemplate);
    return true;
  };

  const handleLaunch = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        campaignName: campaignName.trim(),
        campaigntype: "CSVBROADCAST",
        csvheadersmap,
        headerValues: {},
        bodyValues: {},
        buttonValues: {},
        selectedTemplate,
        phoneNumbers: excelData.map((r) => r.phoneNumber),
        headerMappings: csvheadersmap,
        defaultCountryCode: countryCode,
        templatemediaurl: "",
        exceldata: excelData,
        csvfileurl: "",
        csvfilename: csvFile?.name || "upload.csv",
        currentPhoneNumberId: phoneNoId,
        currentWabaId: wabaId || phoneNoId,
        carouselcardmediaurl: {},
        carouselcardplaceholdervalues: {},
        templatetypelatitude: "",
        templatetypelongtitude: "",
        templatetypelocationname: "",
        templatetypelocationaddress: "",
        senderUserName: getUserName() || "Admin",
        isScheduled: scheduleOn ? "On" : "Off",
        selectedDate: scheduleOn ? scheduleDate : "",
        selectedTime: scheduleOn ? scheduleTime : "",
        projectId,
      };

      const res = await apihubPost(
        "/getInteractedCustomerDetail/send-broadcast",
        payload,
      );
      setSuccessId(res?.campaignid || res?.data?._id || "ok");
    } catch (err) {
      setError(
        err.message ||
          "Launch failed. Sign in to apihub with a valid token to send CSV campaigns.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="biz-panel-page biz-broadcast-page">
      <div className="biz-panel-header">
        <h1>
          <FaFileExcel />
          CSV Broadcast
        </h1>
      </div>

      <div className="biz-broadcast-steps">
        {STEPS.map((label, idx) => (
          <div
            key={label}
            className={`biz-broadcast-step ${
              idx === step ? "active" : idx < step ? "done" : ""
            }`}
          >
            <span className="biz-broadcast-step-num">
              {idx < step ? <FaCheck /> : idx + 1}
            </span>
            <span className="biz-broadcast-step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="biz-broadcast-body">
        {successId ? (
          <div className="biz-broadcast-success">
            <h2>Campaign launched</h2>
            <p>
              CSV campaign &quot;{campaignName}&quot; submitted for{" "}
              {excelData.length} recipient(s).
            </p>
            <Link
              to={businessPath(projectId, "fetchingallcampaign")}
              className="biz-btn-primary"
            >
              View Campaign Report
            </Link>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="biz-broadcast-hero">
                <h2>Campaign Name</h2>
                <p>Pick something that describes your audience &amp; goals.</p>
                <input
                  type="text"
                  placeholder="Enter Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </div>
            )}

            {step === 1 && (
              <div className="biz-csv-upload">
                <div className="biz-csv-upload-head">
                  <h2>Upload CSV File</h2>
                  <button
                    type="button"
                    className="biz-btn-outline biz-csv-sample-btn"
                    onClick={downloadSample}
                  >
                    <FaDownload />
                    Download Sample CSV file
                  </button>
                </div>
                <div
                  className="biz-csv-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                >
                  <p>Drag and drop a CSV file here or click to upload</p>
                  <label className="biz-btn-primary biz-csv-file-label">
                    Choose File
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="biz-csv-file-input"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                  </label>
                  {csvFile && (
                    <p className="biz-csv-file-name">
                      {csvFile.name} · {csvRows.length} rows
                    </p>
                  )}
                </div>
                {csvRows.length > 0 && (
                  <div className="biz-table-wrap">
                    <table className="biz-table">
                      <thead>
                        <tr>
                          {csvHeaders.map((h) => (
                            <th key={h}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 5).map((row, idx) => (
                          <tr key={idx}>
                            {csvHeaders.map((h) => (
                              <td key={h}>{row[h]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvRows.length > 5 && (
                      <p className="biz-panel-hint">
                        Showing first 5 of {csvRows.length} rows
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="biz-csv-mapping">
                <h2>Map CSV Headers to Attributes</h2>
                <label className="biz-csv-country">
                  Default Country Code
                  <input
                    type="text"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    placeholder="91"
                  />
                </label>
                <div className="biz-csv-map-list">
                  {csvHeaders.map((header) => (
                    <div key={header} className="biz-csv-map-row">
                      <span className="biz-csv-map-header">{header}</span>
                      <select
                        value={headerMappings[header]?.field || ""}
                        onChange={(e) =>
                          setHeaderMappings((prev) => ({
                            ...prev,
                            [header]: {
                              ...prev[header],
                              field: e.target.value,
                            },
                          }))
                        }
                      >
                        {ATTRIBUTE_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Default value"
                        value={headerMappings[header]?.default || ""}
                        onChange={(e) =>
                          setHeaderMappings((prev) => ({
                            ...prev,
                            [header]: {
                              ...prev[header],
                              default: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <p className="biz-broadcast-count">
                  {excelData.length} valid phone numbers mapped
                  {!phoneHeader && " — map one column to Phone / Mobile"}
                </p>
              </div>
            )}

            {step === 3 && (
              <CampaignTemplatePicker
                templates={templates}
                selectedTemplate={selectedTemplate}
                onSelect={setSelectedTemplate}
                loading={loadingTemplates}
              />
            )}

            {step === 4 && (
              <div className="biz-broadcast-schedule">
                <h2>Schedule the campaign</h2>
                <p>
                  Schedule the campaign at the favorable time to hit the
                  broadcast.
                </p>
                <label className="biz-broadcast-schedule-toggle">
                  <input
                    type="checkbox"
                    checked={scheduleOn}
                    onChange={(e) => setScheduleOn(e.target.checked)}
                  />
                  Enable scheduling
                </label>
                {scheduleOn && (
                  <div className="biz-broadcast-schedule-fields">
                    <label>
                      Select Date:
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                      />
                    </label>
                    <label>
                      Select Time:
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                      />
                    </label>
                  </div>
                )}
                <div className="biz-broadcast-summary">
                  <h3>Summary</h3>
                  <ul>
                    <li>
                      <strong>Campaign:</strong> {campaignName}
                    </li>
                    <li>
                      <strong>CSV file:</strong> {csvFile?.name || "—"}
                    </li>
                    <li>
                      <strong>Recipients:</strong> {excelData.length}
                    </li>
                    <li>
                      <strong>Template:</strong>{" "}
                      {selectedTemplate?.name || "—"}
                    </li>
                    <li>
                      <strong>Country code:</strong> +{countryCode}
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {error && <p className="biz-panel-error">{error}</p>}

            <div className="biz-broadcast-nav">
              {step > 0 && (
                <button
                  type="button"
                  className="biz-btn-outline"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={submitting}
                >
                  Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext()}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={handleLaunch}
                  disabled={submitting || !canNext()}
                >
                  {submitting ? "Launching…" : "Launch Campaign"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExcelCampaign;
